import { getQuestionnaireType, QUESTIONNAIRE_TYPES } from '@main/shared/browser';
import {
  createColumnHelper,
  Table,
  TableEmptyState,
  useDrawer,
  useTableFiltersQuery,
  useTableSearchQuery,
  useTableSortQuery,
} from '@main/ui';
import { QUESTIONNAIRE_STATUSES } from '@main/vendors';
import { Row } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetVendorQuestionnairesTableSubscription } from './questionnaires-table.subs';
import { GetVendorQuestionnairesTableQuery } from './questionnaires-table.vendor.generated';

type VendorQuestionnaires = GetVendorQuestionnairesTableQuery['vendor_questionnaires'];
type VendorQuestionnaire = VendorQuestionnaires[number];

export const VendorQuestionnairesTable = () => {
  const { t } = useTranslation();
  const { isLoading, data } = useGetVendorQuestionnairesTableSubscription();
  const questionnaires = data?.vendor_questionnaires ?? [];

  const { columns, onRowClick } = useVendorQuestionnairesTableColumns();
  const [columnFilters, setColumnFilters] = useTableFiltersQuery({
    columns,
    searchParam: 'questionnairesFilter',
  });
  const [globalFilter, setGlobalFilter] = useTableSearchQuery({ searchParam: 'search' });
  const [sorting, setSorting] = useTableSortQuery({ searchParam: 'questionnairesSort' });

  const tableItemName = useMemo(() => {
    return {
      singular: t('vendors.questionnaires.heading').toLowerCase(),
      plural: t('vendors.questionnaires.heading').toLowerCase(),
    };
  }, [t]);

  return (
    <Table
      id="vendor-questionnaires"
      entity="vendor-questionnaire"
      data={questionnaires}
      isLoading={isLoading}
      columns={columns}
      itemName={tableItemName}
      renderEmptyState={(props) => <TableEmptyState {...props} />}
      columnFilters={columnFilters}
      onColumnFiltersChange={setColumnFilters}
      globalFilter={globalFilter}
      onGlobalFilterChange={setGlobalFilter}
      sorting={sorting}
      onSortingChange={setSorting}
      onRowClick={onRowClick}
    />
  );
};

const useVendorQuestionnairesTableColumns = () => {
  const { t } = useTranslation();
  const drawer = useDrawer();

  return useMemo(() => {
    const columnHelper = createColumnHelper<VendorQuestionnaire>();

    return {
      onRowClick: (row: Row<VendorQuestionnaire>) =>
        drawer.open({ entity: 'vendor-questionnaire', entityId: row.original.id }),
      columns: [
        columnHelper.columns.status({
          id: 'status',
          enableGlobalFilter: true,
          enableColumnFilter: true,
          enableSorting: true,
          header: t('vendors.questionnaires.tableColumns.status'),
          accessorFn: (data) => {
            const statusMeta = QUESTIONNAIRE_STATUSES[data.status];

            return {
              value: t(statusMeta.value),
              tone: statusMeta.tone,
            };
          },
          size: 120,
        }),
        columnHelper.columns.text({
          id: 'name',
          enableGlobalFilter: true,
          enableColumnFilter: true,
          enableSorting: true,
          enableHiding: false,
          header: t('vendors.questionnaires.tableColumns.name'),
          accessorFn: (data) => data.questionnaire?.name,
        }),
        columnHelper.columns.tag({
          id: 'type',
          enableGlobalFilter: true,
          enableColumnFilter: true,
          enableSorting: true,
          header: t('vendors.questionnaires.tableColumns.type'),
          accessorFn: (data) => {
            const typeMeta = QUESTIONNAIRE_TYPES[getQuestionnaireType(data.questionnaire)];

            return {
              value: t(typeMeta.value),
              colorScheme: typeMeta.colorScheme,
            };
          },
          size: 120,
        }),
        columnHelper.columns.date({
          id: 'sentDate',
          enableColumnFilter: true,
          enableSorting: true,
          header: t('vendors.questionnaires.tableColumns.sentDate'),
          accessorFn: (data) => data.created_at,
          size: 180,
        }),
        columnHelper.columns.date({
          id: 'dueDate',
          enableColumnFilter: true,
          enableSorting: true,
          header: t('vendors.questionnaires.tableColumns.dueDate'),
          accessorFn: (data) => data.due_date,
          size: 180,
        }),
        columnHelper.columns.tag({
          id: 'sentTo',
          enableGlobalFilter: true,
          enableColumnFilter: true,
          enableSorting: true,
          header: t('vendors.questionnaires.tableColumns.sentTo'),
          accessorFn: (data) => ({
            value: data.vendor?.name ?? '',
            label: data.vendor?.name ?? '',
            colorScheme: 'purple',
          }),
          size: 180,
        }),
        columnHelper.columns.avatar({
          id: 'requestedBy',
          enableGlobalFilter: true,
          enableColumnFilter: true,
          enableSorting: true,
          header: t('vendors.questionnaires.tableColumns.requestedBy'),
          variant: 'detailed',
          accessorFn: (data) => {
            return {
              id: data?.sent_by_user?.id ?? '',
              displayName: data?.sent_by_user?.displayName ?? 'Unknown',
            };
          },
          size: 220,
        }),
      ],
    };
  }, [t, drawer]);
};
